import React from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
} from "@hello-pangea/dnd";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_update_check_in_template,
  API_delete_check_in_template_section,
  API_reorder_check_in_template_sections,
} from "../../../constants";

import CheckInTemplateSectionDrawer from "./CheckInTemplateSectionDrawer";


const CADENCE_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Biweekly" },
  { value: "monthly", label: "Monthly" },
  { value: "none", label: "No cadence" },
];


function CheckInTemplateEditor(props) {
  const { template, homeCompany, onTemplateChange } = props;

  const [editingSectionUuid, setEditingSectionUuid] = React.useState(null);
  const [creatingSection, setCreatingSection] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState(template.name);
  const [actionError, setActionError] = React.useState(null);

  React.useEffect(() => {
    setNameDraft(template.name);
  }, [template.name, template.uuid]);

  const lastSavedNameRef = React.useRef(template.name);

  const commitNameIfChanged = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setNameDraft(lastSavedNameRef.current);
      return;
    }
    if (trimmed === lastSavedNameRef.current) return;
    try {
      const res = await postRightworkAPI(API_update_check_in_template, {
        company_id: homeCompany,
        check_in_template_uuid: template.uuid,
        name: trimmed,
      });
      if (res.status === 200) {
        const updated = res.data?.redux?.payload?.check_in_template;
        if (updated) {
          onTemplateChange(updated);
          lastSavedNameRef.current = updated.name;
        }
      }
    } catch (e) {
      setActionError(e?.response?.data?.system?.message || "Couldn't save name.");
      setNameDraft(lastSavedNameRef.current);
    }
  };

  const handleCadenceChange = async (newCadence) => {
    // Optimistic update so the Select doesn't lag behind the click.
    const oldCadence = template.cadence;
    onTemplateChange({ ...template, cadence: newCadence });
    try {
      const res = await postRightworkAPI(API_update_check_in_template, {
        company_id: homeCompany,
        check_in_template_uuid: template.uuid,
        cadence: newCadence,
      });
      if (res.status === 200) {
        onTemplateChange(res.data?.redux?.payload?.check_in_template);
      }
    } catch (e) {
      onTemplateChange({ ...template, cadence: oldCadence });
      setActionError(e?.response?.data?.system?.message || "Couldn't save cadence.");
    }
  };

  const openCreateSectionDrawer = () => {
    if (creatingSection) return;
    // Close any open edit drawer first so we don't end up in mixed state.
    setEditingSectionUuid(null);
    setCreatingSection(true);
  };

  const handleSectionDrawerClose = () => {
    setEditingSectionUuid(null);
    setCreatingSection(false);
  };

  const handleDeleteSection = async (sectionUuid, sectionTitle) => {
    if (!window.confirm(`Delete section "${sectionTitle}"? Its fields will also be removed.`)) {
      return;
    }
    try {
      const res = await postRightworkAPI(API_delete_check_in_template_section, {
        company_id: homeCompany,
        check_in_template_section_uuid: sectionUuid,
      });
      if (res.status === 200) {
        onTemplateChange(res.data?.redux?.payload?.check_in_template);
      }
    } catch (e) {
      setActionError(e?.response?.data?.system?.message || "Couldn't delete section.");
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    // Optimistically reorder locally.
    const reordered = Array.from(template.sections);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    onTemplateChange({ ...template, sections: reordered });

    try {
      const res = await postRightworkAPI(API_reorder_check_in_template_sections, {
        company_id: homeCompany,
        check_in_template_uuid: template.uuid,
        ordered_uuids: reordered.map((s) => s.uuid),
      });
      if (res.status === 200) {
        onTemplateChange(res.data?.redux?.payload?.check_in_template);
      }
    } catch (e) {
      setActionError(e?.response?.data?.system?.message || "Couldn't save section order.");
      // Revert by re-applying the original order.
      onTemplateChange(template);
    }
  };

  const editingSection = editingSectionUuid
    ? template.sections.find((s) => s.uuid === editingSectionUuid)
    : null;

  return (
    <div>
      {/* Template-level controls */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Template name
            </label>
            <TextField
              value={nameDraft}
              onChange={(evt) => setNameDraft(evt.target.value)}
              onBlur={commitNameIfChanged}
              fullWidth
              size="small"
              variant="outlined"
              InputProps={{ sx: { backgroundColor: "white", fontSize: "14px" } }}
              sx={{
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Cadence
            </label>
            <Select
              value={template.cadence}
              onChange={(evt) => handleCadenceChange(evt.target.value)}
              size="small"
              fullWidth
              sx={{
                backgroundColor: "white",
                fontSize: "14px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              }}
            >
              {CADENCE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              Informational only — used to label "Next: weekly" on the profile.
            </p>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">Sections</h2>
        <span className="text-xs text-slate-500">
          Drag to reorder. Section snapshots in past check-ins stay frozen.
        </span>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="sections">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="space-y-2"
            >
              {template.sections.map((section, idx) => (
                <Draggable
                  key={section.uuid}
                  draggableId={section.uuid}
                  index={idx}
                >
                  {(dragProvided, snapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className={`bg-white border rounded-xl p-3 flex items-center gap-3 transition-shadow ${
                        snapshot.isDragging
                          ? "border-violet-400 shadow-md"
                          : "border-slate-200"
                      }`}
                    >
                      <div
                        {...dragProvided.dragHandleProps}
                        className="text-slate-400 hover:text-slate-600 cursor-grab"
                      >
                        <DragIndicatorIcon style={{ fontSize: "20px" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-900">
                            {idx + 1}. {section.title}
                          </span>
                          <span className="text-xs text-slate-500">
                            ({section.fields?.length || 0} {section.fields?.length === 1 ? "field" : "fields"})
                          </span>
                        </div>
                        {section.prompt_help_text && (
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            {section.prompt_help_text}
                          </p>
                        )}
                      </div>
                      <Tooltip title="Edit section" arrow>
                        <IconButton
                          size="small"
                          onClick={() => setEditingSectionUuid(section.uuid)}
                        >
                          <EditIcon style={{ fontSize: "18px" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete section" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSection(section.uuid, section.title)}
                        >
                          <DeleteOutlinedIcon
                            style={{ fontSize: "18px" }}
                            className="text-slate-500 hover:text-rose-600"
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Add section — mirrors the "Add a shift" button pattern in
          ShiftInputsModal: dashed-border click target at the bottom of the
          list. Opens the section drawer in create mode; the actual POST only
          fires when the user clicks Save inside the drawer. Slate dashed
          per design ask (not violet). */}
      <div
        role="button"
        tabIndex={0}
        onClick={openCreateSectionDrawer}
        onKeyDown={(evt) => {
          if (evt.key === "Enter" || evt.key === " ") {
            evt.preventDefault();
            openCreateSectionDrawer();
          }
        }}
        className="w-full mt-3 py-3 border border-dashed border-slate-300 rounded-xl flex items-center justify-center cursor-pointer hover:bg-slate-50 hover:border-slate-400 transition-colors"
      >
        <AddIcon
          style={{
            fontSize: "16px",
            color: "#64748B",
            marginRight: "4px",
          }}
        />
        <p className="text-sm text-slate-500">Add a section</p>
      </div>

      {actionError && (
        <p className="text-sm text-rose-600 mt-3">{actionError}</p>
      )}

      {/* Section drawer — same component for create + edit, switched by
          `isCreating`. Both modes have a footer Save button; the drawer
          closes itself on save in either mode. */}
      <CheckInTemplateSectionDrawer
        open={!!editingSection || creatingSection}
        onClose={handleSectionDrawerClose}
        section={editingSection}
        isCreating={creatingSection}
        template={template}
        homeCompany={homeCompany}
        onTemplateChange={onTemplateChange}
      />
    </div>
  );
}

export default CheckInTemplateEditor;
